import {
  ExtractDocumentTypeFromTypedRxJsonSchema,
  RxJsonSchema,
  toTypedRxJsonSchema,
} from 'rxdb'

import {
  ActivitySupport,
  ActivitySupportList,
  Assistance,
  AssistanceNeeded,
  AssistanceService,
  AssistanceServiceNeeded,
  BehaviorChanges,
  BehaviorChangesList,
  CareIssues,
  CareRelationshipAdvice,
  CareSituation,
  CareWellOrganized,
  ChangesSupportNeeded,
  DepressedRecently,
  DiseaseMeasures,
  DiseaseMeasuresList,
  FallenCareRecently,
  GenerallyPhysicalActive,
  HospitalizedRecently,
  LessEnjoyment,
  LivingEnvironmentAdvice,
  LivingEnvironmentProblems,
  LivingEnvironmentProblemsList,
  LivingEnvironmentSupport,
  MentalDifficulties,
  MentalDifficultiesList,
  MentalHealthCounseling,
  MobilityAreaAdvice,
  MovingDifficulties,
  MovingDifficultiesActivity,
  OrganizationCareAdviceNeeded,
  OtherAssistanceNeeded,
  OtherAssistanceService,
  PhysicalHealthAdvice,
  PhysicalProblems,
  PhysicalProblemsList,
  RelationshipIssues,
  RelativesCareAdvice,
  RelativesCareDifficultiesList,
  RelativesCareHelpFactorsList,
  RelativesIssues,
  RelativesRelationshipIssues,
  ReportingResponsible,
  Responsible,
  SelfSufficiencyAdvice,
  SignsOfHealthRisk,
  SignsOfHealthRiskList,
  SocialContactAdvice,
  SocialContactDifficulties,
  SocialContactDifficultiesList,
  SocialContactSupport,
  SufficientActivitiesSupport,
  SufficientMovingCare,
  SufficientProblemsSupport,
  Therapy,
  TherapyNeeded,
  WithRelativesIssues,
  WithRelativesIssuesPersonInNeed,
} from '@/gql/graphql'
import { addNullToEnum } from '@/system/rxdb/utils.ts'

export const assessmentDetail = {
  version: 0,
  title: 'AssessmentDetail',
  primaryKey: 'rowId',
  type: 'object',
  properties: {
    rowId: {
      maxLength: 36,
      type: 'string',
    },
    updatedAt: {
      type: 'string',
    },
    assessmentId: {
      ref: 'assessment',
      type: 'string',
    },
    reportingResponsible: {
      type: ['string', 'null'],
      enum: addNullToEnum(ReportingResponsible),
    },
    assistanceNeeded: {
      type: ['string', 'null'],
      enum: addNullToEnum(AssistanceNeeded),
    },
    assistances: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(Assistance),
      },
    },
    assistancesMobilityComments: {
      type: ['string', 'null'],
    },
    assistancesDevicesToFoodIntakeComments: {
      type: ['string', 'null'],
    },
    assistancesPersonalHygieneComments: {
      type: ['string', 'null'],
    },
    assistancesPositioningInBedComments: {
      type: ['string', 'null'],
    },
    assistancesPhysicalAidComments: {
      type: ['string', 'null'],
    },
    assistancesDevicesToSupportExcretionComments: {
      type: ['string', 'null'],
    },
    assistancesOtherComments: {
      type: ['string', 'null'],
    },
    assistanceServicesNeeded: {
      type: ['string', 'null'],
      enum: addNullToEnum(AssistanceServiceNeeded),
    },
    assistanceServices: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(AssistanceService),
      },
    },
    assistanceServicesOtherComments: {
      type: ['string', 'null'],
    },
    otherAssistancesNeeded: {
      type: ['string', 'null'],
      enum: addNullToEnum(OtherAssistanceNeeded),
    },
    otherAssistancesService: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(OtherAssistanceService),
      },
    },
    otherAssistancesServiceOtherComments: {
      type: ['string', 'null'],
    },
    therapiesNeeded: {
      type: ['string', 'null'],
      enum: addNullToEnum(TherapyNeeded),
    },
    therapies: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(Therapy),
      },
    },
    therapiesOtherComments: {
      type: ['string', 'null'],
    },
    careWellOrganized: {
      type: ['string', 'null'],
      enum: addNullToEnum(CareWellOrganized),
    },
    organizationCareAdviceNeeded: {
      type: ['string', 'null'],
      enum: addNullToEnum(OrganizationCareAdviceNeeded),
    },
    organizationCareAdviceTopics: {
      type: ['string', 'null'],
    },
    organizationCareAdviceComments: {
      type: ['string', 'null'],
    },
    movingDifficulties: {
      type: ['string', 'null'],
      enum: addNullToEnum(MovingDifficulties),
    },
    movingDifficultiesActivity: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(MovingDifficultiesActivity),
      },
    },
    movingDifficultiesActivityOtherComments: {
      type: ['string', 'null'],
    },
    sufficientMovingCare: {
      type: ['string', 'null'],
      enum: addNullToEnum(SufficientMovingCare),
    },
    fallenCareRecently: {
      type: ['string', 'null'],
      enum: addNullToEnum(FallenCareRecently),
    },
    generallyPhysicalActive: {
      type: ['string', 'null'],
      enum: addNullToEnum(GenerallyPhysicalActive),
    },
    mobilityAreaAdvice: {
      type: ['string', 'null'],
      enum: addNullToEnum(MobilityAreaAdvice),
    },
    mobilityAreaAdviceTopics: {
      type: ['string', 'null'],
    },
    mobilityAreaAdviceComments: {
      type: ['string', 'null'],
    },
    mentalDifficulties: {
      type: ['string', 'null'],
      enum: addNullToEnum(MentalDifficulties),
    },
    mentalDifficultiesList: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(MentalDifficultiesList),
      },
    },
    mentalDifficultiesListOtherComments: {
      type: ['string', 'null'],
    },
    behaviorChanges: {
      type: ['string', 'null'],
      enum: addNullToEnum(BehaviorChanges),
    },
    behaviorChangesList: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(BehaviorChangesList),
      },
    },
    behaviorChangesListOtherComments: {
      type: ['string', 'null'],
    },
    changesSupportNeeded: {
      type: ['string', 'null'],
      enum: addNullToEnum(ChangesSupportNeeded),
    },
    depressedRecently: {
      type: ['string', 'null'],
      enum: addNullToEnum(DepressedRecently),
    },
    lessEnjoyment: {
      type: ['string', 'null'],
      enum: addNullToEnum(LessEnjoyment),
    },
    mentalHealthCounseling: {
      type: ['string', 'null'],
      enum: addNullToEnum(MentalHealthCounseling),
    },
    mentalHealthCounselingTopics: {
      type: ['string', 'null'],
    },
    mentalHealthCounselingComments: {
      type: ['string', 'null'],
    },
    physicalProblems: {
      type: ['string', 'null'],
      enum: addNullToEnum(PhysicalProblems),
    },
    physicalProblemsList: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(PhysicalProblemsList),
      },
    },
    physicalProblemsListOtherComments: {
      type: ['string', 'null'],
    },
    diseaseMeasures: {
      type: ['string', 'null'],
      enum: addNullToEnum(DiseaseMeasures),
    },
    diseaseMeasuresList: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(DiseaseMeasuresList),
      },
    },
    diseaseMeasuresListOtherComments: {
      type: ['string', 'null'],
    },
    diseaseMeasuresResponsible: {
      type: ['object', 'null'],
      properties: {
        medication: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        medicationOtherComments: {
          type: ['string', 'null'],
        },
        bodyConditionMonitoring: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        bodyConditionMonitoringOtherComments: {
          type: ['string', 'null'],
        },
        handlingBodyHuggingAids: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        handlingBodyHuggingAidsOtherComments: {
          type: ['string', 'null'],
        },
        dressingWoundsCare: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        dressingWoundsCareOtherComments: {
          type: ['string', 'null'],
        },
        therapy: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        therapyOtherComments: {
          type: ['string', 'null'],
        },
        doctorVisits: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        doctorVisitsOtherComments: {
          type: ['string', 'null'],
        },
        dietDueToIllness: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        dietDueToIllnessOtherComments: {
          type: ['string', 'null'],
        },
        other: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        otherOtherComments: {
          type: ['string', 'null'],
        },
      },
    },
    sufficientProblemsSupport: {
      type: ['string', 'null'],
      enum: addNullToEnum(SufficientProblemsSupport),
    },
    hospitalizedRecently: {
      type: ['string', 'null'],
      enum: addNullToEnum(HospitalizedRecently),
    },
    signsOfHealthRisk: {
      type: ['string', 'null'],
      enum: addNullToEnum(SignsOfHealthRisk),
    },
    signsOfHealthRiskList: {
      type: ['array', 'null'],
      items: {
        type: ['string', 'null'],
        enum: addNullToEnum(SignsOfHealthRiskList),
      },
    },
    signsOfHealthRiskListSpecificRisksComments: {
      type: ['string', 'null'],
    },
    physicalHealthAdvice: {
      type: ['string', 'null'],
      enum: addNullToEnum(PhysicalHealthAdvice),
    },
    physicalHealthAdviceTopics: {
      type: ['string', 'null'],
    },
    physicalHealthAdviceComments: {
      type: ['string', 'null'],
    },
    activitySupport: {
      type: ['string', 'null'],
      enum: addNullToEnum(ActivitySupport),
    },
    activitySupportList: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(ActivitySupportList),
      },
    },
    activitySupportListHygieneOtherComments: {
      type: ['string', 'null'],
    },
    activitySupportListDressingOtherComments: {
      type: ['string', 'null'],
    },
    activitySupportListNutritionOtherComments: {
      type: ['string', 'null'],
    },
    activitySupportListExcretionOtherComments: {
      type: ['string', 'null'],
    },
    activitySupportListHousekeepingOtherComments: {
      type: ['string', 'null'],
    },
    activitySupportPerson: {
      type: ['object', 'null'],
      properties: {
        washUpperBody: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        washUpperBodyOtherComments: {
          type: ['string', 'null'],
        },
        combingShaving: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        combingShavingOtherComments: {
          type: ['string', 'null'],
        },
        oralCare: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        oralCareOtherComments: {
          type: ['string', 'null'],
        },
        washIntimateArea: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        washIntimateAreaOtherComments: {
          type: ['string', 'null'],
        },
        shower: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        showerOtherComments: {
          type: ['string', 'null'],
        },
        hairWash: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        hairWashOtherComments: {
          type: ['string', 'null'],
        },
        hygieneOther: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        hygieneOtherOtherComments: {
          type: ['string', 'null'],
        },
        dressingUpperBody: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        dressingUpperBodyOtherComments: {
          type: ['string', 'null'],
        },
        dressingLowerBody: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        dressingLowerBodyOtherComments: {
          type: ['string', 'null'],
        },
        dressingOther: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        dressingOtherOtherComments: {
          type: ['string', 'null'],
        },
        drink: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        drinkOtherComments: {
          type: ['string', 'null'],
        },
        eat: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        eatOtherComments: {
          type: ['string', 'null'],
        },
        nutritionOther: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        nutritionOtherOtherComments: {
          type: ['string', 'null'],
        },
        toilet: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        toiletOtherComments: {
          type: ['string', 'null'],
        },
        urinaryIncontinence: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        urinaryIncontinenceOtherComments: {
          type: ['string', 'null'],
        },
        fecalIncontinence: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        fecalIncontinenceOtherComments: {
          type: ['string', 'null'],
        },
        excretionOther: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        excretionOtherOtherComments: {
          type: ['string', 'null'],
        },
        shopping: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        shoppingOtherComments: {
          type: ['string', 'null'],
        },
        cooking: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        cookingOtherComments: {
          type: ['string', 'null'],
        },
        cleaning: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        cleaningOtherComments: {
          type: ['string', 'null'],
        },
        housekeepingOther: {
          type: ['array', 'null'],
          items: {
            type: 'string',
            enum: Object.values(Responsible),
          },
        },
        housekeepingOtherOtherComments: {
          type: ['string', 'null'],
        },
      },
    },
    sufficientActivitiesSupport: {
      type: ['string', 'null'],
      enum: addNullToEnum(SufficientActivitiesSupport),
    },
    selfSufficiencyAdvice: {
      type: ['string', 'null'],
      enum: addNullToEnum(SelfSufficiencyAdvice),
    },
    selfSufficiencyAdviceTopics: {
      type: ['string', 'null'],
    },
    selfSufficiencyAdviceComments: {
      type: ['string', 'null'],
    },
    socialContactDifficulties: {
      type: ['string', 'null'],
      enum: addNullToEnum(SocialContactDifficulties),
    },
    socialContactDifficultiesList: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(SocialContactDifficultiesList),
      },
    },
    socialContactDifficultiesListOtherComments: {
      type: ['string', 'null'],
    },
    socialContactSupport: {
      type: ['string', 'null'],
      enum: addNullToEnum(SocialContactSupport),
    },
    socialContactAdvice: {
      type: ['string', 'null'],
      enum: addNullToEnum(SocialContactAdvice),
    },
    socialContactAdviceTopics: {
      type: ['string', 'null'],
    },
    socialContactAdviceComments: {
      type: ['string', 'null'],
    },
    livingEnvironmentProblems: {
      type: ['string', 'null'],
      enum: addNullToEnum(LivingEnvironmentProblems),
    },
    livingEnvironmentProblemsList: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(LivingEnvironmentProblemsList),
      },
    },
    livingEnvironmentProblemsListOtherComments: {
      type: ['string', 'null'],
    },
    livingEnvironmentSupport: {
      type: ['string', 'null'],
      enum: addNullToEnum(LivingEnvironmentSupport),
    },
    livingEnvironmentAdvice: {
      type: ['string', 'null'],
      enum: addNullToEnum(LivingEnvironmentAdvice),
    },
    livingEnvironmentAdviceTopics: {
      type: ['string', 'null'],
    },
    livingEnvironmentAdviceComments: {
      type: ['string', 'null'],
    },
    relationshipIssues: {
      type: ['string', 'null'],
      enum: addNullToEnum(RelationshipIssues),
    },
    withRelativesIssues: {
      type: ['string', 'null'],
      enum: addNullToEnum(WithRelativesIssues),
    },
    withRelativesIssuesPersonInNeed: {
      type: ['string', 'null'],
      enum: addNullToEnum(WithRelativesIssuesPersonInNeed),
    },
    relativesRelationshipIssues: {
      type: ['string', 'null'],
      enum: addNullToEnum(RelativesRelationshipIssues),
    },
    relativesIssues: {
      type: ['string', 'null'],
      enum: addNullToEnum(RelativesIssues),
    },
    careIssues: {
      type: ['string', 'null'],
      enum: addNullToEnum(CareIssues),
    },
    careRelationshipAdvice: {
      type: ['string', 'null'],
      enum: addNullToEnum(CareRelationshipAdvice),
    },
    careRelationshipAdviceTopics: {
      type: ['string', 'null'],
    },
    careRelationshipAdviceComments: {
      type: ['string', 'null'],
    },
    careSituation: {
      type: ['string', 'null'],
      enum: addNullToEnum(CareSituation),
    },
    relativesCareDifficultiesList: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(RelativesCareDifficultiesList),
      },
    },
    relativesCareDifficultiesListOtherComments: {
      type: ['string', 'null'],
    },
    relativesCareHelpFactorsList: {
      type: ['array', 'null'],
      items: {
        type: 'string',
        enum: Object.values(RelativesCareHelpFactorsList),
      },
    },
    relativesCareHelpFactorsListOtherComments: {
      type: ['string', 'null'],
    },
    relativesCareAdvice: {
      type: ['string', 'null'],
      enum: addNullToEnum(RelativesCareAdvice),
    },
    relativesCareAdviceTopics: {
      type: ['string', 'null'],
    },
    relativesCareAdviceComments: {
      type: ['string', 'null'],
    },
  },
  required: ['rowId', 'updatedAt', 'assessmentId'],
} as const

const schemaTyped = toTypedRxJsonSchema(assessmentDetail)

export type AssessmentDetail = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>

export const assessmentDetailSchema: RxJsonSchema<AssessmentDetail> =
  assessmentDetail
